import { Alert, Box } from '@mui/material'
import { useRef, useState } from 'react'
import { insertAtCursor } from '@/utils/templateTokens'
import type { SendChannel } from '@/services/notificationService'
import ChannelPanel from './ChannelPanel'
import TemplateVariablePalette from './TemplateVariablePalette'
import { sortForPanels } from './channelKinds'
import type { ComposeChecks } from './composeChecks'
import { ChannelDraft, ChannelDrafts, EMPTY_DRAFT } from './sendContent'

type Props = {
  accountId: number
  channels: SendChannel[]
  tickedIds: number[]
  onTick: (id: number, ticked: boolean) => void
  drafts: ChannelDrafts
  onDraft: (id: number, draft: ChannelDraft) => void
  checks: Pick<ComposeChecks, 'unknown' | 'malformed'>
  customerName?: string
}

/**
 * The compose part of a send: the channel panels side by side, the variable
 * palette and the variable warnings. The send window and the alert tree's send
 * node both render it, so the two cannot drift.
 */
const ChannelCompose = ({
  accountId,
  channels,
  tickedIds,
  onTick,
  drafts,
  onDraft,
  checks,
  customerName,
}: Props) => {
  // the palette inserts into the panel that last had focus
  const [focusedId, setFocusedId] = useState<number | null>(null)
  const inputs = useRef<Record<number, HTMLTextAreaElement | null>>({})
  const panels = sortForPanels(channels)
  const tickedPanelIds = panels
    .filter(c => tickedIds.includes(c.channelId))
    .map(c => c.channelId)

  const insertVariable = (variable: string) => {
    const id =
      focusedId !== null && tickedPanelIds.includes(focusedId)
        ? focusedId
        : tickedPanelIds[0]
    if (id === undefined) return
    const input = inputs.current[id]
    const body = drafts[id]?.body ?? ''
    const { next, caret } = insertAtCursor(
      body,
      input?.selectionStart ?? body.length,
      variable
    )
    onDraft(id, { ...(drafts[id] ?? EMPTY_DRAFT), body: next })
    requestAnimationFrame(() => {
      input?.focus()
      input?.setSelectionRange(caret, caret)
    })
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: `repeat(${Math.min(Math.max(panels.length, 1), 3)}, minmax(0, 1fr))`,
          },
        }}
      >
        {panels.map(channel => (
          <ChannelPanel
            key={channel.channelId}
            channel={channel}
            ticked={tickedIds.includes(channel.channelId)}
            onTick={ticked => onTick(channel.channelId, ticked)}
            draft={drafts[channel.channelId] ?? EMPTY_DRAFT}
            onDraft={draft => onDraft(channel.channelId, draft)}
            customerName={customerName}
            onFocus={() => setFocusedId(channel.channelId)}
            inputRef={el => {
              inputs.current[channel.channelId] = el
            }}
          />
        ))}
      </Box>
      {tickedPanelIds.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TemplateVariablePalette
            accountId={accountId}
            onInsert={insertVariable}
          />
        </Box>
      )}
      {checks.malformed.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          תבנית משתנה שגויה: {checks.malformed.join(', ')}
        </Alert>
      )}
      {checks.malformed.length === 0 && checks.unknown.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          משתנים לא מורשים: {checks.unknown.join(', ')}
        </Alert>
      )}
    </>
  )
}

export default ChannelCompose
